import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Layers,
  Globe,
  FileOutput,
  RefreshCw,
  Users,
  Smartphone,
  Zap,
  Mail,
} from 'lucide-react';
import SeoHead from '../../components/SeoHead';

const ABOUT_FEATURES = [
  { icon: Layers, titleKey: 'about.feature1Title', descKey: 'about.feature1Desc' },
  { icon: Globe, titleKey: 'about.feature2Title', descKey: 'about.feature2Desc' },
  { icon: FileOutput, titleKey: 'about.feature3Title', descKey: 'about.feature3Desc' },
  { icon: RefreshCw, titleKey: 'about.feature4Title', descKey: 'about.feature4Desc' },
];

const ABOUT_STATS = [
  { valueKey: 'about.stat1Value', labelKey: 'about.stat1Label', icon: Users },
  { valueKey: 'about.stat2Value', labelKey: 'about.stat2Label', icon: Smartphone },
  { valueKey: 'about.stat3Value', labelKey: 'about.stat3Label', icon: Zap },
];

export default function About() {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-hidden">
      <SeoHead page="about" />
      {/* Hero */}
      <section className="relative px-4 sm:px-6 pt-10 sm:pt-16 pb-14 sm:pb-20 bg-gradient-to-b from-primary-50/80 to-white dark:from-gray-900 dark:to-gray-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(37,99,235,0.12),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.1),transparent)] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.15]">
            {t('about.heroTitle')}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('about.heroSubtitle')}
          </p>
          <div className="mt-8">
            <Link
              to="/register"
              className="btn btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-shadow"
            >
              {t('about.heroCta')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Origin */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-4">
            {t('about.originLabel')}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            {t('about.originTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            {t('about.originPara1')}
          </p>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-10">
            {t('about.originPara2')}
          </p>
          <p className="text-lg font-medium text-gray-900 dark:text-white mb-12">
            {t('about.originTagline')}
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {ABOUT_FEATURES.map(({ icon: Icon, titleKey, descKey }) => (
              <div
                key={titleKey}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 p-6"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 mb-3">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t(titleKey)}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{t(descKey)}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/register"
              className="btn btn-primary text-sm py-2.5 px-4 rounded-xl"
            >
              {t('about.ctaReady')}
            </Link>
            <Link
              to="/#features"
              className="btn btn-secondary text-sm py-2.5 px-4 rounded-xl"
            >
              {t('about.ctaExamples')}
            </Link>
          </div>
        </div>
      </section>

      {/* How we got here */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            {t('about.howTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            {t('about.howPara1')}
          </p>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            {t('about.howPara2')}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
            {t('about.statsTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12">
            {ABOUT_STATS.map(({ valueKey, labelKey, icon: Icon }) => (
              <div key={labelKey} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mb-4">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                  {t(valueKey)}
                </div>
                <div className="mt-1 text-gray-600 dark:text-gray-400 font-medium">{t(labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we are */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            {t('about.whoTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
            {t('about.whoDesc')}
          </p>
          <a
            href="mailto:support@buildmate.com"
            className="btn btn-primary inline-flex items-center gap-2 py-2.5 px-5 rounded-xl"
          >
            <Mail className="h-4 w-4" />
            {t('about.contactCta')}
          </a>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white dark:bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
            {t('about.closingPara')}
          </p>
          <Link
            to="/register"
            className="btn btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-xl shadow-lg shadow-primary-500/25"
          >
            {t('about.closingCta')}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
